/** HTTP-клиент: токены, обновление доступа, единый разбор ошибок. */

import type { ApiErrorBody, ApiErrorCode } from './types'

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

const ACCESS_KEY = 'sudoku.access'
const REFRESH_KEY = 'sudoku.refresh'

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.detail ?? `Ошибка запроса (${status})`)
    this.status = status
    this.code = body.code ?? 'unknown'
    this.body = body
  }
}

/** Сеть недоступна — в отличие от ApiError это повод повторить запрос. */
export class NetworkError extends Error {
  constructor() {
    super('Нет связи с сервером')
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY)
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY)
  },
  save(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access)
    localStorage.setItem(REFRESH_KEY, refresh)
  },
  saveAccess(access: string) {
    localStorage.setItem(ACCESS_KEY, access)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH'
  body?: unknown
  /** Внутренний флаг: не пытаться обновлять токен повторно. */
  retried?: boolean
  auth?: boolean
}

async function parseBody(response: Response): Promise<ApiErrorBody> {
  try {
    return (await response.json()) as ApiErrorBody
  } catch {
    return {}
  }
}

/** Обновить access по refresh. Возвращает false, если сессия мертва. */
async function refreshAccess(): Promise<boolean> {
  const refresh = tokens.refresh
  if (!refresh) return false

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  })
  if (!response.ok) {
    tokens.clear()
    return false
  }

  const data = (await response.json()) as { access: string; refresh?: string }
  tokens.saveAccess(data.access)
  // Бэкенд ротирует refresh-токены, поэтому новый нужно сохранить.
  if (data.refresh) tokens.save(data.access, data.refresh)
  return true
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, retried = false } = options

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (auth && tokens.access) {
    headers.Authorization = `Bearer ${tokens.access}`
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new NetworkError()
  }

  // Токен протух — один раз пробуем обновить и повторить.
  if (response.status === 401 && auth && !retried) {
    if (await refreshAccess()) {
      return request<T>(path, { ...options, retried: true })
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, await parseBody(response))
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}
